import React, { useState } from 'react';

/**
 * 글자 없이 아이콘만 있는 버튼 (닫기, 더보기, 편집 연필 등). 사용법은 docs/ui-components.md 참고.
 *
 * variant
 * - ghost   : 바탕 없음. 마우스를 올리면 연회색 원 (기본)
 * - line    : 흰 바탕 + 회색 테두리
 * - inverse : 어두운 바탕(남색·주황 헤더 등) 위의 흰 아이콘
 */
export type IconButtonVariant = 'ghost' | 'line' | 'inverse';
export type IconButtonSize = 'small' | 'medium' | 'large';

export interface IconButtonProps {
  icon: React.ReactNode;
  /** 화면 낭독기가 읽을 이름. 아이콘만 있어서 꼭 필요하다. 예: '닫기' */
  ariaLabel: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  disabled?: boolean;
  /** 마우스를 올리면 나오는 설명. 없으면 ariaLabel을 쓴다. */
  title?: string;
  type?: 'button' | 'submit' | 'reset';
  style?: React.CSSProperties;
}

const SIZE_PX: Record<IconButtonSize, number> = {
  small: 28,
  medium: 36,
  large: 44,
};

const VARIANT_STYLES: Record<IconButtonVariant, { base: React.CSSProperties; hover: React.CSSProperties }> = {
  ghost: {
    base: { backgroundColor: 'transparent', color: 'var(--Color-text-strong, #0D0D0D)', border: 'none' },
    hover: { backgroundColor: 'var(--Color-fill-hover, #F5F5F5)' },
  },
  line: {
    base: {
      backgroundColor: 'var(--Color-background-normal, white)',
      color: 'var(--Color-text-strong, #0D0D0D)',
      border: '1px solid var(--Color-line-normal, #F0F0F0)',
    },
    hover: { backgroundColor: 'var(--Color-fill-hover, #F5F5F5)' },
  },
  inverse: {
    base: { backgroundColor: 'transparent', color: 'var(--Color-text-inverse, white)', border: 'none' },
    hover: { backgroundColor: 'rgba(255, 255, 255, 0.16)' },
  },
};

const FOCUS_RING = '0 0 0 2px var(--Color-background-normal, white), 0 0 0 4px var(--Color-fill-brand-color, #FF6F0F)';

const isKeyboardFocus = (element: HTMLElement) => {
  try {
    return element.matches(':focus-visible');
  } catch {
    return false;
  }
};

const IconButton: React.FC<IconButtonProps> = ({
  icon,
  ariaLabel,
  onClick,
  variant = 'ghost',
  size = 'medium',
  disabled = false,
  title,
  type = 'button',
  style,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const px = SIZE_PX[size];
  const variantStyle = VARIANT_STYLES[variant];

  return (
    <button
      type={type}
      aria-label={ariaLabel}
      title={title ?? ariaLabel}
      disabled={disabled}
      onClick={(e) => {
        if (disabled) return;
        onClick?.(e);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={(e) => setIsFocusVisible(isKeyboardFocus(e.currentTarget))}
      onBlur={() => setIsFocusVisible(false)}
      style={{
        width: px,
        height: px,
        minWidth: px,
        padding: 0,
        margin: 0,
        borderRadius: '9999px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        outline: 'none',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
        lineHeight: 0,
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
        ...variantStyle.base,
        ...(!disabled && isHovered ? variantStyle.hover : null),
        ...(isFocusVisible ? { boxShadow: FOCUS_RING } : null),
        ...style,
      }}
    >
      {icon}
    </button>
  );
};

export default IconButton;
